import React, { useState, useEffect, useCallback } from 'react';
import { 
  Mail, Send, Inbox, Search, FileText, User, Loader2, 
  ChevronLeft, Trash2, RefreshCw, CheckCircle, AlertCircle,
  PenTool, Sparkles, Paperclip, X, File, Check, Building2,
  Wand2, MessageSquare, Lock, Copy, RotateCcw, Trash, Download
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fonction pour télécharger un fichier
const downloadFile = async (url, filename) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Erreur de téléchargement');
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Erreur téléchargement:', error);
    alert('Erreur lors du téléchargement du fichier');
  }
};

const Messaging = () => {
  const [activeTab, setActiveTab] = useState('inbox'); // Afficher la boîte de réception par défaut
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [dossierDocuments, setDossierDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [destinataireSuggestions, setDestinataireSuggestions] = useState([]);
  const [clientDepartement, setClientDepartement] = useState(null);
  const [identificationBloc, setIdentificationBloc] = useState('');
  const [showAiAssistant, setShowAiAssistant] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiDraft, setAiDraft] = useState(null); // Brouillon généré par l'IA
  const [hasEmailAccount, setHasEmailAccount] = useState(false); // Compte email configuré
  const [emailAccountInfo, setEmailAccountInfo] = useState(null); // Infos du compte email
  const [syncing, setSyncing] = useState(false); // Synchronisation IMAP en cours
  const [trashEmails, setTrashEmails] = useState([]); // Emails dans la corbeille
  const [trashCount, setTrashCount] = useState(0); // Nombre d'emails dans la corbeille
  const [showHtmlVersion, setShowHtmlVersion] = useState(true); // Afficher version HTML par défaut
  
  // Formulaire d'envoi
  const [formData, setFormData] = useState({
    to_email: '',
    to_name: '',
    subject: '',
    body: '',
    dossier_id: null,
    template_type: null,
    include_client_email: true
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientSearch, setShowClientSearch] = useState(false);
  const [notification, setNotification] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // State pour la sélection de dossier/client
  const [dossiers, setDossiers] = useState([]);
  const [searchDossier, setSearchDossier] = useState('');
  const [selectedDossierForReply, setSelectedDossierForReply] = useState(null);
  const [loadingDossiers, setLoadingDossiers] = useState(false);

  // Charger les templates et vérifier le compte email
  useEffect(() => {
    loadTemplates();
    loadUnreadCount();
    checkEmailAccount();
    // Charger la boîte de réception automatiquement au démarrage
    loadInbox();
  }, []);

  // Vérifier si un compte email est configuré
  const checkEmailAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/email-accounts/default`, { headers });
      const data = await res.json();
      setHasEmailAccount(!!data && !!data.email);
      if (data && data.email) {
        setEmailAccountInfo(data);
      }
    } catch (error) {
      console.error('Erreur vérification compte email:', error);
      setHasEmailAccount(false);
    }
  };

  // Charger les emails quand on change d'onglet
  useEffect(() => {
    if (activeTab === 'inbox') {
      loadInbox();
    } else if (activeTab === 'sent') {
      loadSent();
    } else if (activeTab === 'trash') {
      loadTrash();
    }
  }, [activeTab]);

  const loadTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/templates`, { headers });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
    }
  };

  const loadInbox = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/inbox`, { headers });
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Erreur chargement inbox:', error);
    } finally {
      setLoading(false);
    }
  };

  // Synchroniser la boîte de réception via IMAP
  const syncInbox = async () => {
    if (!hasEmailAccount) {
      setNotification({ type: 'error', message: 'Aucun compte email configuré' });
      return;
    }
    
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/inbox/sync`, { 
        method: 'POST',
        headers 
      });
      const data = await res.json();
      
      if (res.ok) {
        setNotification({ 
          type: 'success', 
          message: data.synced > 0 
            ? `${data.synced} nouveau(x) email(s) synchronisé(s)` 
            : 'Boîte de réception à jour'
        });
        // Recharger la liste
        await loadInbox();
      } else {
        setNotification({ type: 'error', message: data.detail || 'Erreur de synchronisation' });
      }
    } catch (error) {
      console.error('Erreur sync inbox:', error);
      setNotification({ type: 'error', message: 'Erreur de synchronisation' });
    } finally {
      setSyncing(false);
    }
  };

  const loadSent = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/sent`, { headers });
      const data = await res.json();
      setEmails(data.emails || []);
    } catch (error) {
      console.error('Erreur chargement sent:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/unread-count`, { headers });
      const data = await res.json();
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  // Charger la corbeille
  const loadTrash = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/trash`, { headers });
      const data = await res.json();
      setTrashEmails(data || []);
      setTrashCount(data?.length || 0);
    } catch (error) {
      console.error('Erreur chargement corbeille:', error);
    } finally {
      setLoading(false);
    }
  };

  // Restaurer un email depuis la corbeille
  const restoreEmail = async (emailId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/email/${emailId}/restore`, {
        method: 'POST',
        headers
      });
      if (res.ok) {
        setNotification({ type: 'success', message: 'Email restauré' });
        loadTrash();
        loadInbox();
      }
    } catch (error) {
      console.error('Erreur restauration:', error);
      setNotification({ type: 'error', message: 'Erreur lors de la restauration' });
    }
  };

  // Supprimer définitivement un email
  const permanentDelete = async (emailId) => {
    if (!window.confirm('Supprimer définitivement cet email ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messaging/email/${emailId}?permanent=true`, {
        method: 'DELETE',
        headers
      });
      if (res.ok) {
        setNotification({ type: 'success', message: 'Email supprimé définitivement' });
        loadTrash();
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  // Vider la corbeille
  const emptyTrash = async () => {
    if (!window.confirm('Vider la corbeille ? Cette action est irréversible.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/messaging/trash/empty`, {
        method: 'DELETE',
        headers
      });
      const data = await res.json();
      if (res.ok) {
        setNotification({ type: 'success', message: `${data.deleted_count} email(s) supprimé(s)` });
        setTrashEmails([]);
        setTrashCount(0);
      }
    } catch (error) {
      console.error('Erreur vidage corbeille:', error);
    }
  };

  // Recherche de client
  const searchClient = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/search-client`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query })
      });
      const data = await res.json();
      setSearchResults(data.clients || []);
    } catch (error) {
      console.error('Erreur recherche:', error);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery) {
        searchClient(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, searchClient]);

  // Sélectionner un client
  const selectClient = async (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, dossier_id: client.dossier_id, include_client_email: true }));
    setShowClientSearch(false);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedDocuments([]);
    setIdentificationBloc('');
    
    // Générer le bloc d'identification
    try {
      const res = await fetch(`${API_URL}/api/messaging/generate-identification?dossier_id=${client.dossier_id}`, {
        method: 'POST',
        headers
      });
      const data = await res.json();
      
      // Stocker le bloc pour utilisation ultérieure
      setIdentificationBloc(data.bloc || '');
      
      // Insérer le bloc dans le body
      setFormData(prev => ({ ...prev, body: data.bloc + '\n\n' }));
    } catch (error) {
      console.error('Erreur génération bloc:', error);
    }
    
    // Charger les documents du dossier
    try {
      const res = await fetch(`${API_URL}/api/messaging/dossier/${client.dossier_id}/documents`, { headers });
      const data = await res.json();
      setDossierDocuments(data.documents || []);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
    }
    
    // Charger les suggestions de destinataires
    try {
      const res = await fetch(`${API_URL}/api/messaging-config/suggestions/${client.dossier_id}`, { headers });
      const data = await res.json();
      setDestinataireSuggestions(data.suggestions || []);
      setClientDepartement(data.departement);
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    }
  };

  // Sélectionner un destinataire suggéré
  const selectDestinataire = (dest) => {
    setFormData(prev => ({
      ...prev,
      to_email: dest.email,
      to_name: dest.name
    }));
  };

  // Toggle document selection
  const toggleDocument = (doc) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(d => d.id === doc.id);
      if (isSelected) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        return [...prev, doc];
      }
    });
  };

  // Appliquer un template
  const applyTemplate = async (templateId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/generate-template`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          template_type: templateId,
          dossier_id: selectedClient?.dossier_id
        })
      });
      const data = await res.json();
      
      setFormData(prev => ({
        ...prev,
        subject: data.subject || prev.subject,
        body: data.body || prev.body,
        template_type: templateId
      }));
    } catch (error) {
      console.error('Erreur template:', error);
    }
  };

  // Assistant IA pour reformuler le message
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      setNotification({ type: 'error', message: 'Décrivez ce que vous voulez dire' });
      return;
    }
    
    setAiGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/ai-reformulate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: aiPrompt,
          dossier_id: selectedClient?.dossier_id,
          subject: formData.subject
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Stocker le brouillon pour validation
        setAiDraft({
          subject: data.subject || formData.subject,
          body: data.body
        });
      } else {
        setNotification({ type: 'error', message: data.detail || 'Erreur IA' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la génération' });
    } finally {
      setAiGenerating(false);
    }
  };

  // Valider le brouillon IA
  const acceptAiDraft = () => {
    if (aiDraft) {
      setFormData(prev => ({
        ...prev,
        subject: aiDraft.subject,
        body: aiDraft.body
      }));
      setAiDraft(null);
      setShowAiAssistant(false);
      setAiPrompt('');
      setNotification({ type: 'success', message: 'Mail copié dans le formulaire' });
    }
  };

  // Répondre à un email
  const handleReplyEmail = (email) => {
    const replySubject = email.subject?.startsWith('Re:') ? email.subject : `Re: ${email.subject}`;
    const replyBody = `\n\n\n-------- Message original --------\nDe: ${email.from_name || email.from_email}\nDate: ${new Date(email.created_at).toLocaleString('fr-FR')}\nObjet: ${email.subject}\n\n${email.body}`;
    
    setFormData({
      to_email: email.from_email || '',
      to_name: email.from_name || '',
      subject: replySubject,
      body: replyBody,
      dossier_id: email.dossier_id || null,
      template_type: null,
      include_client_email: false
    });
    setSelectedEmail(null);
    setActiveTab('compose');
    setNotification({ type: 'info', message: 'Réponse préparée - Modifiez et envoyez' });
  };

  // Transférer un email
  const handleForwardEmail = (email) => {
    const fwdSubject = email.subject?.startsWith('Tr:') ? email.subject : `Tr: ${email.subject}`;
    let fwdBody = `\n\n\n-------- Message transféré --------\nDe: ${email.from_name || email.from_email}\nÀ: ${email.to_email}\nDate: ${new Date(email.created_at).toLocaleString('fr-FR')}\nObjet: ${email.subject}\n\n${email.body}`;
    
    // Ajouter les pièces jointes si présentes
    if (email.attachments && email.attachments.length > 0) {
      fwdBody += `\n\n[${email.attachments.length} pièce(s) jointe(s)]`;
    }
    
    setFormData({
      to_email: '',
      to_name: '',
      subject: fwdSubject,
      body: fwdBody,
      dossier_id: email.dossier_id || null,
      template_type: null,
      include_client_email: false
    });
    setSelectedEmail(null);
    setActiveTab('compose');
    setNotification({ type: 'info', message: 'Email prêt à transférer - Ajoutez le destinataire' });
  };

  // Rechercher des dossiers
  const searchDossiers = async (query) => {
    if (!query || query.length < 2) {
      setDossiers([]);
      return;
    }
    
    setLoadingDossiers(true);
    try {
      const res = await fetch(`${API_URL}/api/dossiers?search=${encodeURIComponent(query)}&limit=10`, { headers });
      const data = await res.json();
      setDossiers(data || []);
    } catch (error) {
      console.error('Erreur recherche dossiers:', error);
    } finally {
      setLoadingDossiers(false);
    }
  };

  // Sélectionner un dossier et insérer les infos client
  const selectDossierForReply = (dossier) => {
    setSelectedDossierForReply(dossier);
    setSearchDossier('');
    setDossiers([]);
    
    // Construire le bloc d'identification client
    const clientInfo = buildClientInfoBlock(dossier);
    
    // Insérer au début du body (avant le message original)
    setFormData(prev => ({
      ...prev,
      dossier_id: dossier.id,
      body: clientInfo + prev.body
    }));
    
    setNotification({ type: 'success', message: `Client ${dossier.identite?.nom} ${dossier.identite?.prenom} ajouté` });
  };

  // Construire le bloc d'informations client
  const buildClientInfoBlock = (dossier) => {
    const id = dossier.identite || {};
    const coord = dossier.coordonnees || {};
    
    let block = `══════════════════════════════════════
IDENTIFICATION DU BÉNÉFICIAIRE
══════════════════════════════════════
Référence dossier : ${dossier.reference || 'N/A'}
Nom : ${id.nom || ''} ${id.prenom || ''}
Date de naissance : ${id.date_naissance ? new Date(id.date_naissance).toLocaleDateString('fr-FR') : 'N/A'}
N° Sécurité Sociale : ${id.numero_secu || 'N/A'}
`;
    
    if (coord.telephone) {
      block += `Téléphone : ${coord.telephone}\n`;
    }
    if (coord.email) {
      block += `Email : ${coord.email}\n`;
    }
    if (coord.adresse) {
      block += `Adresse : ${coord.adresse}`;
      if (coord.code_postal || coord.ville) {
        block += `, ${coord.code_postal || ''} ${coord.ville || ''}`;
      }
      block += '\n';
    }
    
    block += `══════════════════════════════════════\n\n`;
    
    return block;
  };

  // Retirer le dossier sélectionné
  const clearSelectedDossier = () => {
    setSelectedDossierForReply(null);
  };

  // Reformuler le brouillon
  const reformulateAiDraft = () => {
    setAiDraft(null);
    // On garde le prompt pour que l'utilisateur puisse le modifier
  };

  // Envoyer l'email
  const sendEmail = async () => {
    if (!formData.to_email || !formData.subject || !formData.body) {
      setNotification({ type: 'error', message: 'Veuillez remplir tous les champs obligatoires' });
      return;
    }
    
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/messaging/send`, {
        method: 'POST',
        headers,
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Email envoyé avec succès !' });
        // Reset form
        setFormData({
          to_email: '',
          to_name: '',
          subject: '',
          body: '',
          dossier_id: null,
          template_type: null
        });
        setSelectedClient(null);
      } else {
        setNotification({ type: 'error', message: data.detail || 'Erreur lors de l\'envoi' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de l\'envoi' });
    } finally {
      setSending(false);
    }
  };

  // Voir un email
  const viewEmail = async (email) => {
    setSelectedEmail(email);
    setShowHtmlVersion(true); // Afficher version originale par défaut
    
    // Marquer comme lu si c'est un email reçu
    if (email.type === 'received' && !email.read) {
      try {
        await fetch(`${API_URL}/api/messaging/email/${email.id}/read`, {
          method: 'POST',
          headers
        });
        // Mettre à jour l'email localement
        email.read = true;
        setSelectedEmail({...email});
        // Mettre à jour la liste des emails
        setEmails(prev => prev.map(e => e.id === email.id ? {...e, read: true} : e));
        // Recharger le compteur
        loadUnreadCount();
      } catch (error) {
        console.error('Erreur:', error);
      }
    }
  };

  // Supprimer un email
  const deleteEmail = async (emailId) => {
    try {
      const res = await fetch(`${API_URL}/api/messaging/email/${emailId}`, {
        method: 'DELETE',
        headers
      });
      
      if (res.ok) {
        setNotification({ type: 'success', message: 'Email déplacé vers la corbeille' });
        setSelectedEmail(null);
        // Recharger la liste
        if (activeTab === 'inbox') {
          loadInbox();
        } else if (activeTab === 'sent') {
          loadSent();
        }
        // Mettre à jour le compteur de la corbeille
        loadTrash();
      } else {
        setNotification({ type: 'error', message: 'Erreur lors de la suppression' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  };

  // Notification auto-dismiss
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4 text-sm">
      {/* Sidebar */}
      <div className="w-56 bg-white rounded-xl shadow-lg border border-gray-200 p-3 flex flex-col">
        <h2 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
          <Mail className="w-4 h-4 text-emerald-600" />
          Messagerie
        </h2>
        
        {/* Compte email connecté */}
        {emailAccountInfo && (
          <div className="mb-3 px-2 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <div className="text-xs text-blue-600 font-medium truncate" title={emailAccountInfo.email}>
              📧 {emailAccountInfo.email}
            </div>
          </div>
        )}
        
        <nav className="space-y-1">
          <button
            onClick={() => { setActiveTab('compose'); setSelectedEmail(null); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-xs ${
              activeTab === 'compose' 
                ? 'bg-emerald-100 text-emerald-700 font-semibold' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <PenTool className="w-4 h-4" />
            Nouveau message
          </button>
          
          <button
            onClick={() => { setActiveTab('inbox'); setSelectedEmail(null); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-xs ${
              activeTab === 'inbox' 
                ? 'bg-emerald-100 text-emerald-700 font-semibold' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Inbox className="w-4 h-4" />
            Boîte de réception
            {unreadCount > 0 && (
              <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
          
          <button
            onClick={() => { setActiveTab('sent'); setSelectedEmail(null); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-xs ${
              activeTab === 'sent' 
                ? 'bg-emerald-100 text-emerald-700 font-semibold' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Send className="w-4 h-4" />
            Messages envoyés
          </button>
          
          {/* Corbeille */}
          <button
            onClick={() => { setActiveTab('trash'); setSelectedEmail(null); loadTrash(); }}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition text-xs ${
              activeTab === 'trash' 
                ? 'bg-red-100 text-red-700 font-semibold' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Trash className="w-4 h-4" />
            Corbeille
            {trashCount > 0 && (
              <span className="ml-auto bg-gray-400 text-white text-xs px-1.5 py-0.5 rounded-full">
                {trashCount}
              </span>
            )}
          </button>
        </nav>
        
        {/* Templates */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <h3 className="text-xs font-semibold text-gray-700 mb-2">Templates</h3>
          <div className="space-y-0.5">
            {templates.map(t => (
              <button
                key={t.id}
                onClick={() => { setActiveTab('compose'); applyTemplate(t.id); }}
                className="w-full text-left px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-lg transition truncate"
              >
                📋 {t.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Zone principale */}
      <div className="flex-1 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col">
        {/* Notification */}
        {notification && (
          <div className={`px-3 py-2 flex items-center gap-2 text-xs ${
            notification.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {notification.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            {notification.message}
            <button onClick={() => setNotification(null)} className="ml-auto">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Composer */}
        {activeTab === 'compose' && !selectedEmail && (
          <div className="flex-1 p-4 overflow-y-auto">
            <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-emerald-600" />
              Nouveau message
            </h2>
            
            {/* Recherche client */}
            <div className="mb-4 p-3 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span className="font-semibold text-emerald-800 text-xs">Assistant IA</span>
              </div>
              
              {selectedClient ? (
                <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-emerald-300">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-xs">{selectedClient.display}</p>
                      <p className="text-xs text-gray-500">Dossier {selectedClient.statut}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Bouton Documents */}
                    <button
                      onClick={() => document.getElementById('documents-section')?.scrollIntoView({ behavior: 'smooth' })}
                      className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-xs font-medium"
                    >
                      <FileText className="w-3 h-3" />
                      Documents ({dossierDocuments.length})
                    </button>
                    <button
                      onClick={() => { setSelectedClient(null); setDestinataireSuggestions([]); setDossierDocuments([]); }}
                      className="text-gray-400 hover:text-red-500 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setShowClientSearch(true); }}
                      onFocus={() => setShowClientSearch(true)}
                      placeholder="Rechercher un client par nom..."
                      className="flex-1 px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-xs"
                    />
                  </div>
                  
                  {showClientSearch && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-60 overflow-y-auto">
                      {searchResults.map(client => (
                        <button
                          key={client.dossier_id}
                          onClick={() => selectClient(client)}
                          className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-gray-100 last:border-b-0 text-xs"
                        >
                          <p className="font-semibold">{client.display}</p>
                          <p className="text-xs text-gray-500">Dossier {client.statut}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {searching && (
                    <div className="absolute right-3 top-2">
                      <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-emerald-700 mt-2">
                💡 Sélectionnez un client pour insérer automatiquement son bloc d'identification
              </p>
            </div>
            
            {/* Suggestions de destinataires - Toujours afficher l'email du client */}
            {selectedClient && (
              <div className="mb-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-blue-800 text-xs">
                    Destinataires {clientDepartement && `(Dép. ${clientDepartement})`}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {/* Email du client en premier */}
                  {selectedClient.email && (
                    <button
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          to_email: selectedClient.email,
                          to_name: selectedClient.display,
                          body: '', // Pas de bloc identification pour le client
                          include_client_email: false
                        }));
                      }}
                      className={`px-2 py-1 rounded-lg text-xs transition flex items-center gap-1 ${
                        formData.to_email === selectedClient.email
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-blue-300 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      <User className="w-3 h-3" />
                      {selectedClient.display?.split(' ')[0]} (client)
                    </button>
                  )}
                  
                  {/* Organismes suggérés */}
                  {destinataireSuggestions.map((dest, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        // Ajouter le bloc identification quand on sélectionne un organisme
                        const bloc = identificationBloc || '';
                        setFormData(prev => ({
                          ...prev,
                          to_email: dest.email,
                          to_name: dest.name,
                          include_client_email: true,
                          body: prev.body.includes('IDENTIFICATION') ? prev.body : (bloc + '\n\n')
                        }));
                      }}
                      className={`px-2 py-1 rounded-lg text-xs transition flex items-center gap-1 ${
                        formData.to_email === dest.email
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white border border-blue-200 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      {dest.type === 'departement' && '🏛️'}
                      {dest.type === 'caisse' && '🏢'}
                      {dest.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Sélecteur de dossier/client pour réponse */}
            <div className="mb-3 p-3 bg-purple-50 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4 text-purple-600" />
                <span className="font-semibold text-purple-800 text-xs">
                  Lier à un dossier client (optionnel)
                </span>
              </div>
              
              {selectedDossierForReply ? (
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-purple-300">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-900">
                      {selectedDossierForReply.identite?.nom} {selectedDossierForReply.identite?.prenom}
                    </span>
                    <span className="text-xs text-purple-500">
                      ({selectedDossierForReply.reference})
                    </span>
                  </div>
                  <button
                    onClick={clearSelectedDossier}
                    className="p-1 text-purple-400 hover:text-red-500 hover:bg-red-50 rounded transition"
                    title="Retirer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={searchDossier}
                    onChange={(e) => {
                      setSearchDossier(e.target.value);
                      searchDossiers(e.target.value);
                    }}
                    placeholder="Rechercher un dossier (nom, prénom, référence...)"
                    className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  {loadingDossiers && (
                    <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-purple-500" />
                  )}
                  
                  {/* Résultats de recherche */}
                  {dossiers.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-purple-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {dossiers.map(dossier => (
                        <button
                          key={dossier.id}
                          onClick={() => selectDossierForReply(dossier)}
                          className="w-full text-left px-3 py-2 hover:bg-purple-50 border-b border-gray-100 last:border-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {dossier.identite?.nom} {dossier.identite?.prenom}
                            </span>
                            <span className="text-xs text-gray-500">{dossier.reference}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {dossier.coordonnees?.email || 'Pas d\'email'}
                            {dossier.caisse && ` • ${dossier.caisse}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              <p className="text-xs text-purple-600 mt-2">
                💡 Sélectionnez un dossier pour insérer automatiquement les informations du client au début du mail
              </p>
            </div>
            
            {/* Formulaire */}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email destinataire *
                  </label>
                  <input
                    type="email"
                    value={formData.to_email}
                    onChange={(e) => setFormData({ ...formData, to_email: e.target.value })}
                    placeholder="exemple@organisme.fr"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nom destinataire
                  </label>
                  <input
                    type="text"
                    value={formData.to_name}
                    onChange={(e) => setFormData({ ...formData, to_name: e.target.value })}
                    placeholder="Nom de l'organisme"
                    className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-xs"
                  />
                </div>
              </div>
              
              {/* Option mettre le client en copie */}
              {selectedClient && selectedClient.email && formData.to_email !== selectedClient.email && (
                <label className="flex items-center gap-2 text-sm text-gray-700 bg-blue-50 px-3 py-2 rounded-lg border border-blue-200">
                  <input
                    type="checkbox"
                    checked={formData.include_client_email}
                    onChange={(e) => setFormData({ ...formData, include_client_email: e.target.checked })}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span>Mettre le client en copie ({selectedClient.email})</span>
                </label>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Objet *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="Objet du message"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message *
                </label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  rows="16"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm leading-relaxed"
                  placeholder="Votre message..."
                />
              </div>
              
              {/* Documents du dossier */}
              {selectedClient && (
                <div id="documents-section" className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Paperclip className="w-4 h-4 text-gray-600" />
                    <span className="font-semibold text-gray-700 text-xs">Documents du dossier</span>
                    {selectedDocuments.length > 0 && (
                      <span className="ml-auto text-xs text-emerald-600 font-medium">
                        {selectedDocuments.length} sélectionné(s)
                      </span>
                    )}
                  </div>
                  {dossierDocuments.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {dossierDocuments.map(doc => {
                        const isSelected = selectedDocuments.some(d => d.id === doc.id);
                        return (
                          <button
                            key={doc.id}
                            onClick={() => toggleDocument(doc)}
                            className={`flex items-center gap-2 p-2 rounded-lg border transition-all text-left group ${
                              isSelected 
                                ? 'bg-emerald-100 border-emerald-400 text-emerald-800' 
                                : 'bg-white border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition ${
                              isSelected ? 'bg-emerald-500' : 'bg-gray-100 group-hover:bg-emerald-200'
                            }`}>
                              {isSelected ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : (
                                <File className="w-3 h-3 text-gray-500 group-hover:text-emerald-600" />
                              )}
                            </div>
                            <span className="truncate text-xs font-medium">{doc.nom}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-3 text-gray-500">
                      <FileText className="w-6 h-6 mx-auto mb-1 text-gray-300" />
                      <p className="text-xs">Aucun document dans ce dossier</p>
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex items-center justify-between pt-3">
                {selectedDocuments.length > 0 ? (
                  <div className="flex items-center gap-2 text-emerald-600">
                    <Paperclip className="w-4 h-4" />
                    <span className="text-sm font-medium">{selectedDocuments.length} document(s) à joindre</span>
                  </div>
                ) : (
                  <div></div>
                )}
                
                <div className="flex items-center gap-2">
                  {/* Bouton Assistant IA */}
                  <button
                    onClick={() => setShowAiAssistant(!showAiAssistant)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                      showAiAssistant 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    }`}
                  >
                    <Wand2 className="w-4 h-4" />
                    Rédiger avec IA
                  </button>
                  
                  {/* Bouton copier le mail */}
                  <button
                    onClick={() => {
                      const mailContent = `À: ${formData.to_email}\nObjet: ${formData.subject}\n\n${formData.body}`;
                      navigator.clipboard.writeText(mailContent);
                      setNotification({ type: 'success', message: '✅ Mail copié ! Collez-le dans votre boîte mail personnelle.' });
                      setTimeout(() => setNotification(null), 4000);
                    }}
                    disabled={!formData.body}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
                    title="Copier le mail pour l'envoyer depuis votre boîte personnelle"
                  >
                    <Copy className="w-4 h-4" />
                    Copier
                  </button>
                  
                  {/* Bouton envoyer - actif si compte email configuré */}
                  {hasEmailAccount ? (
                    <button
                      onClick={sendEmail}
                      disabled={sending || !formData.to_email || !formData.subject || !formData.body}
                      className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="send-email-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                      Envoyer
                    </button>
                  ) : (
                    <div className="relative group">
                      <button
                        disabled={true}
                        className="flex items-center gap-2 px-5 py-2 bg-gray-400 text-white rounded-lg text-sm font-semibold cursor-not-allowed opacity-60"
                      >
                        <Lock className="w-4 h-4" />
                        Envoyer
                      </button>
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition whitespace-nowrap z-10">
                        📧 Pas de compte email configuré.<br/>
                        Allez dans Configuration &gt; Comptes Email
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Assistant IA Panel */}
              {showAiAssistant && (
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
                      <Wand2 className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-purple-900">Assistant de rédaction</h4>
                      <p className="text-xs text-purple-600">
                        {aiDraft ? 'Voici le mail généré. Validez ou reformulez.' : 'Décrivez ce que vous voulez dire, l\'IA rédigera le mail'}
                      </p>
                    </div>
                  </div>
                  
                  {!aiDraft ? (
                    <>
                      <textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        rows="3"
                        placeholder="Ex: Je veux demander un réexamen du dossier car la situation familiale a changé, ou signaler un changement d'adresse..."
                        className="w-full px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm mb-3"
                      />
                      
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-purple-600">
                          <MessageSquare className="w-3 h-3 inline mr-1" />
                          L'IA ajoutera automatiquement le bloc d'identification du client
                        </p>
                        <button
                          onClick={generateWithAI}
                          disabled={aiGenerating || !aiPrompt.trim()}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition disabled:opacity-50"
                        >
                          {aiGenerating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Sparkles className="w-4 h-4" />
                          )}
                          Générer le mail
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Aperçu du brouillon généré */}
                      <div className="bg-white rounded-lg border border-purple-200 p-3 mb-3">
                        <div className="mb-2">
                          <span className="text-xs font-medium text-gray-500">Objet :</span>
                          <p className="text-sm font-medium text-gray-900">{aiDraft.subject}</p>
                        </div>
                        <div>
                          <span className="text-xs font-medium text-gray-500">Message :</span>
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap mt-1 max-h-48 overflow-y-auto">
                            {aiDraft.body}
                          </pre>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={reformulateAiDraft}
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Reformuler
                        </button>
                        <button
                          onClick={acceptAiDraft}
                          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 transition"
                        >
                          <Check className="w-4 h-4" />
                          Valider et copier
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Liste des emails (inbox/sent) */}
        {(activeTab === 'inbox' || activeTab === 'sent') && !selectedEmail && (
          <div className="flex-1 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                {activeTab === 'inbox' ? <Inbox className="w-6 h-6" /> : <Send className="w-6 h-6" />}
                {activeTab === 'inbox' ? 'Boîte de réception' : 'Messages envoyés'}
              </h2>
              <div className="flex items-center gap-2">
                {activeTab === 'inbox' && hasEmailAccount && (
                  <button
                    onClick={syncInbox}
                    disabled={syncing}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm disabled:opacity-50"
                    data-testid="sync-inbox-btn"
                  >
                    {syncing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {syncing ? 'Synchronisation...' : 'Synchroniser'}
                  </button>
                )}
                {activeTab === 'inbox' && hasEmailAccount && (
                  <button
                    onClick={async () => {
                      if (window.confirm('Cela va re-télécharger tous les emails pour récupérer les pièces jointes manquantes. Continuer ?')) {
                        try {
                          const res = await fetch(`${API_URL}/api/messaging/inbox/resync`, {
                            method: 'POST',
                            headers
                          });
                          const data = await res.json();
                          setNotification({ type: 'success', message: data.message });
                          syncInbox();
                        } catch (e) {
                          setNotification({ type: 'error', message: 'Erreur lors de la resynchronisation' });
                        }
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg transition text-sm"
                    title="Forcer la re-synchronisation pour récupérer les pièces jointes"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Re-sync PJ
                  </button>
                )}
                <button
                  onClick={() => activeTab === 'inbox' ? loadInbox() : loadSent()}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  title="Actualiser"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
                </div>
              ) : emails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Mail className="w-12 h-12 mb-3 text-gray-300" />
                  <p>Aucun email</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {emails.map(email => (
                    <div
                      key={email.id}
                      className={`group relative flex items-start px-6 py-4 hover:bg-gray-50 transition ${
                        !email.read ? 'bg-emerald-50' : ''
                      }`}
                    >
                      <button
                        onClick={() => viewEmail(email)}
                        className="flex-1 text-left"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {!email.read && (
                                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                              )}
                              <p className={`font-semibold truncate ${!email.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                {activeTab === 'inbox' ? email.from_name : email.to_name || email.to_email}
                              </p>
                            </div>
                            <p className="text-sm text-gray-900 truncate mt-1 flex items-center gap-2">
                              {email.subject}
                              {email.attachments && email.attachments.length > 0 && (
                                <span className="inline-flex items-center gap-1 text-xs text-gray-400" title={`${email.attachments.length} pièce(s) jointe(s)`}>
                                  <Paperclip className="w-3 h-3" />
                                  {email.attachments.length}
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-500 truncate mt-1">
                              {email.body?.substring(0, 100)}...
                            </p>
                          </div>
                          <div className="ml-4 text-right">
                            <p className="text-xs text-gray-500">
                              {new Date(email.created_at).toLocaleDateString('fr-FR')}
                            </p>
                            {email.status === 'sent' && (
                              <span className="inline-flex items-center gap-1 text-xs text-green-600 mt-1">
                                <CheckCircle className="w-3 h-3" />
                                Envoyé
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteEmail(email.id); }}
                        className="absolute right-4 top-4 p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vue Corbeille */}
        {activeTab === 'trash' && !selectedEmail && (
          <div className="flex-1 flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Trash className="w-6 h-6 text-red-500" />
                Corbeille
              </h2>
              <div className="flex items-center gap-2">
                {trashEmails.length > 0 && (
                  <button
                    onClick={emptyTrash}
                    className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition text-sm"
                  >
                    <Trash2 className="w-4 h-4" />
                    Vider la corbeille
                  </button>
                )}
                <button
                  onClick={loadTrash}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                  title="Actualiser"
                >
                  <RefreshCw className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="px-6 py-2 bg-amber-50 border-b border-amber-200 text-amber-700 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Les emails sont conservés 30 jours avant d'être supprimés définitivement.
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                </div>
              ) : trashEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Trash className="w-12 h-12 mb-3 text-gray-300" />
                  <p>La corbeille est vide</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {trashEmails.map(email => (
                    <div
                      key={email.id}
                      className="group relative flex items-start px-6 py-4 hover:bg-gray-50 transition bg-gray-50/50"
                    >
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">
                              {email.type === 'received' 
                                ? email.from_name || email.from_email 
                                : email.to_name || email.to_email
                              }
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              email.type === 'received' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                            }`}>
                              {email.type === 'received' ? 'Reçu' : 'Envoyé'}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400">
                            Supprimé le {new Date(email.deleted_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">{email.subject}</p>
                        <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{email.body}</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1 ml-4 opacity-0 group-hover:opacity-100 transition">
                        <button
                          onClick={() => restoreEmail(email.id)}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Restaurer"
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => permanentDelete(email.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title="Supprimer définitivement"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vue email détaillée - Style Outlook */}
        {selectedEmail && (
          <div className="flex-1 flex flex-col h-full overflow-hidden">
            {/* Header avec actions */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center gap-3 bg-white shrink-0">
              <button
                onClick={() => setSelectedEmail(null)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                title="Retour"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 truncate flex-1">
                {selectedEmail.subject || '(Sans objet)'}
              </h2>
              
              {/* Actions email */}
              <div className="flex items-center gap-1">
                {/* Répondre */}
                <button
                  onClick={() => handleReplyEmail(selectedEmail)}
                  className="flex items-center gap-1.5 px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm font-medium"
                  title="Répondre"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  Répondre
                </button>
                
                {/* Transférer */}
                <button
                  onClick={() => handleForwardEmail(selectedEmail)}
                  className="flex items-center gap-1.5 px-3 py-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition text-sm font-medium"
                  title="Transférer"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  Transférer
                </button>
                
                {/* Supprimer */}
                <button
                  onClick={() => deleteEmail(selectedEmail.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                  title="Supprimer"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Corps du mail - scrollable */}
            <div className="flex-1 overflow-y-auto bg-gray-50">
              <div className="max-w-4xl mx-auto p-6">
                {/* Informations expéditeur/destinataire */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4">
                  <div className="p-4 flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0">
                      {(selectedEmail.type === 'received' ? selectedEmail.from_name : selectedEmail.to_name || selectedEmail.to_email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-gray-900 text-lg">
                          {selectedEmail.type === 'received' ? selectedEmail.from_name || selectedEmail.from_email : selectedEmail.to_name || selectedEmail.to_email}
                        </p>
                        <span className="text-sm text-gray-500">
                          {new Date(selectedEmail.created_at).toLocaleString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500">
                        {selectedEmail.type === 'received' 
                          ? `De: ${selectedEmail.from_email}` 
                          : `À: ${selectedEmail.to_email}`
                        }
                      </p>
                      {selectedEmail.cc_email && (
                        <p className="text-sm text-gray-400">
                          Cc: {selectedEmail.cc_email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Pièces jointes - EN HAUT */}
                {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Paperclip className="w-5 h-5 text-gray-500" />
                      <h4 className="font-semibold text-gray-700">
                        Pièces jointes ({selectedEmail.attachments.length})
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {selectedEmail.attachments.map((att, idx) => {
                        // Déterminer l'icône et la couleur selon le type
                        const isPDF = att.content_type?.includes('pdf') || att.filename?.toLowerCase().endsWith('.pdf');
                        const isImage = att.content_type?.includes('image');
                        const isWord = att.filename?.toLowerCase().match(/\.(doc|docx)$/);
                        const isExcel = att.filename?.toLowerCase().match(/\.(xls|xlsx)$/);
                        
                        let iconBg = 'bg-blue-100';
                        let iconColor = 'text-blue-600';
                        let iconHoverBg = 'group-hover:bg-blue-200';
                        
                        if (isPDF) { iconBg = 'bg-red-100'; iconColor = 'text-red-600'; iconHoverBg = 'group-hover:bg-red-200'; }
                        else if (isImage) { iconBg = 'bg-green-100'; iconColor = 'text-green-600'; iconHoverBg = 'group-hover:bg-green-200'; }
                        else if (isWord) { iconBg = 'bg-blue-100'; iconColor = 'text-blue-600'; iconHoverBg = 'group-hover:bg-blue-200'; }
                        else if (isExcel) { iconBg = 'bg-emerald-100'; iconColor = 'text-emerald-600'; iconHoverBg = 'group-hover:bg-emerald-200'; }
                        
                        // Formater la taille
                        const formatSize = (bytes) => {
                          if (!bytes) return '';
                          if (bytes < 1024) return `${bytes} o`;
                          if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
                          return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
                        };
                        
                        return (
                          <button
                            key={att.id || idx}
                            onClick={() => downloadFile(`${API_URL}/api/messaging/attachment/${att.id}`, att.filename || 'document')}
                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition group w-full text-left"
                          >
                            <div className={`w-12 h-12 ${iconBg} rounded-lg flex items-center justify-center ${iconHoverBg} transition`}>
                              {isPDF ? (
                                <svg className={`w-6 h-6 ${iconColor}`} fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3zm0 4.5c1.5 0 2.5-.5 2.5-1.5h-5c0 1 1 1.5 2.5 1.5zM14 17h2v-2h-2v2z"/>
                                </svg>
                              ) : (
                                <File className={`w-6 h-6 ${iconColor}`} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate" title={att.filename}>
                                {att.filename || 'Document'}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                {att.size && <span>{formatSize(att.size)}</span>}
                                {isPDF && <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded">PDF</span>}
                                {isImage && <span className="px-1.5 py-0.5 bg-green-100 text-green-600 rounded">Image</span>}
                                {isWord && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded">Word</span>}
                                {isExcel && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-600 rounded">Excel</span>}
                              </div>
                            </div>
                            <div className="text-blue-500 opacity-0 group-hover:opacity-100 transition">
                              <Download className="w-5 h-5" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Contenu du mail */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  {/* Toggle Texte / Version originale */}
                  {selectedEmail.html_body && (
                    <div className="flex items-center gap-2 mb-4 pb-3 border-b">
                      <span className="text-sm text-gray-500">Affichage :</span>
                      <button
                        onClick={() => setShowHtmlVersion(false)}
                        className={`px-3 py-1 text-sm rounded-lg transition ${!showHtmlVersion ? 'bg-emerald-100 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        Texte
                      </button>
                      <button
                        onClick={() => setShowHtmlVersion(true)}
                        className={`px-3 py-1 text-sm rounded-lg transition ${showHtmlVersion ? 'bg-emerald-100 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
                      >
                        Version originale (avec logos)
                      </button>
                    </div>
                  )}
                  
                  {showHtmlVersion && selectedEmail.html_body ? (
                    <div 
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: selectedEmail.html_body }}
                    />
                  ) : (
                    <div 
                      className="prose prose-sm max-w-none text-gray-800"
                      style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: '1.6' }}
                    >
                      {selectedEmail.body}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messaging;
